import React from "react";
import PageHeader from "../../components/common/PageHeader";
import { FiCopy } from "react-icons/fi";
import { FaGooglePay, FaRupeeSign } from "react-icons/fa";
import { SiPhonepe } from "react-icons/si";
import { RiBankFill } from "react-icons/ri";
import { HiOutlineCurrencyRupee } from "react-icons/hi";

const Payments = () => {
  const paymentMethods = [
    {
      id: 1,
      type: "UPI ID",
      value: "9876543210@ybl",
      icon: <HiOutlineCurrencyRupee className="text-purple-500 text-xl" />,
      copyText: "9876543210@ybl",
      bgColor: "bg-purple-50",
    },
    {
      id: 2,
      type: "Google Pay",
      value: "+91 9876543210",
      icon: <FaGooglePay className="text-blue-500 text-xl" />,
      copyText: "+91 9876543210",
      bgColor: "bg-blue-50",
    },
    {
      id: 3,
      type: "PhonePe",
      value: "+91 9876543210",
      icon: <SiPhonepe className="text-indigo-600 text-xl" />,
      copyText: "+91 9876543210",
      bgColor: "bg-indigo-50",
    },
    {
      id: 4,
      type: "Bank Transfer",
      value: "A/C No: 1234567890, IFSC: SBIN0123456, Bank: State Bank of India",
      icon: <RiBankFill className="text-green-600 text-xl" />,
      copyText:
        "Account No: 1234567890, IFSC: SBIN0123456, Bank: State Bank of India",
      bgColor: "bg-green-50",
    },
  ];

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    // You can add a toast notification here
    alert("Copied to clipboard!");
  };

  return (
    <React.Fragment>
      <PageHeader pageName={"Payment Options"} />

      <div className="bg-gray-50 py-6 px-4 lg:px-12">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Make a Payment
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose from multiple secure payment options
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* QR Code Section */}
          <div className="bg-white rounded-2xl shadow-md p-8 flex flex-col items-center">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaRupeeSign size={30} className="text-blue-600" />
              </div>
              <h2 className="tracking-wide text-2xl font-semibold text-gray-800">
                Scan & Pay
              </h2>
              <p className="tracking-wide text-gray-500 mt-2">
                Instant payment using any UPI app
              </p>
            </div>

            <div className="bg-white p-5 rounded-xl border-2 border-dashed border-gray-200 mb-6">
              <img
                src="https://generate-wiget-v2.uniqode.com/Customisation/Templates/template-9.webp"
                alt="UPI QR Code"
                className="w-64 h-64 mx-auto"
              />
            </div>

            <div className="flex flex-wrap justify-center gap-3 mt-4 tracking-wide">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Google Pay
              </span>
              <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                PhonePe
              </span>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                Paytm
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                BHIM
              </span>
            </div>
          </div>

          {/* Payment Methods Section */}
          <div className="space-y-6 tracking-wide">
            <div className="bg-white rounded-2xl shadow-md p-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">
                Other Payment Methods
              </h3>

              <div className="space-y-4">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`${method.bgColor} rounded-xl p-5 transition-all hover:shadow-sm`}
                  >
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center shadow-sm mr-4">
                        {method.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">
                          {method.type}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">
                          {method.value}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCopy(method.copyText)}
                        className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        title="Copy to clipboard"
                      >
                        <FiCopy className="text-lg" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Note */}
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-xl shadow-md p-5">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-amber-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-amber-800">
                    Important Note
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    After payment, please share the transaction details via{" "}
                    <span className="font-semibold text-green-600">
                      WhatsApp: 9337233933
                    </span>{" "}
                    /{" "}
                    <span className="font-semibold text-blue-600">
                      Email: icvcerification2011@gmail.com
                    </span>{" "}
                    for confirmation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Payments;
